import { differenceInYears } from 'date-fns';

import { RootState } from '../../types/graph';
import { buildOnboardingContext } from './buildContext';
import { getBaseFlow, isOffboardingStep } from './computeFlow';
import {
  TRANSFORMERS,
  skipSelfIdentificationForMinors,
  skipSelfIdentificationWhenExplicit,
  skipConsultationTime,
  handleMbpIneligible,
  type FlowTransformer,
} from './transformers';
import { StepId } from './types';

type TransformerDebugInfo = {
  name: string;
  before: StepId[];
  after: StepId[];
  changed: boolean;
  inputs: Record<string, unknown>;
};

type DateOfBirthDebugInfo = {
  raw: string | null;
  parsed: string | null;
  parseValid: boolean;
  ageYears: number | null;
  isUnder16: boolean;
  isEligibleForMonBilanPrevention: boolean;
};

const parseDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateOfBirthDebugInfo = (
  dateOfBirth: string | null,
): DateOfBirthDebugInfo => {
  const parsed = parseDate(dateOfBirth);
  if (!parsed) {
    return {
      raw: dateOfBirth,
      parsed: null,
      parseValid: false,
      ageYears: null,
      isUnder16: false,
      isEligibleForMonBilanPrevention: false,
    };
  }

  const ageYears = differenceInYears(new Date(), parsed);
  const inRange = (min: number, max: number) =>
    ageYears >= min && ageYears <= max;

  return {
    raw: dateOfBirth,
    parsed: parsed.toISOString(),
    parseValid: true,
    ageYears,
    isUnder16: ageYears < 16,
    isEligibleForMonBilanPrevention:
      inRange(18, 25) || inRange(45, 50) || inRange(60, 65) || inRange(70, 75),
  };
};

const getPatientInfoCompletionBreakdown = (state: RootState) => {
  const patient = state.consultation.patient;
  return {
    firstNamePresent: Boolean(patient?.firstName?.trim()),
    lastNamePresent: Boolean(patient?.lastName?.trim()),
    dateOfBirthPresent: Boolean(patient?.dateOfBirth),
    sexPresent: Boolean(patient?.sex?.trim()),
  };
};

const getTransformerInputs = (
  transformer: FlowTransformer,
  flowBefore: StepId[],
  ctx: ReturnType<typeof buildOnboardingContext>,
  dateOfBirthDebug: DateOfBirthDebugInfo,
) => {
  switch (transformer) {
    case skipSelfIdentificationForMinors:
      return {
        patientDateOfBirth: ctx.patientDateOfBirth,
        dateOfBirth: dateOfBirthDebug,
      };
    case skipSelfIdentificationWhenExplicit:
      return {
        selfParam: ctx.selfParam,
        isSelfParamExplicit:
          ctx.selfParam === 'true' || ctx.selfParam === 'false',
      };
    case skipConsultationTime:
      return {
        isInvitation: ctx.isInvitation,
        noAppointment: ctx.noAppointment,
      };
    case handleMbpIneligible:
      return {
        patientOnboarding: ctx.patientOnboarding,
        patientDateOfBirth: ctx.patientDateOfBirth,
        dateOfBirth: dateOfBirthDebug,
        patientInfoStepIndex: flowBefore.findIndex(
          (step) => step === 'patient-information',
        ),
      };
    default:
      return { context: ctx };
  }
};

export const getOnOffboardingDebugInfo = (state: RootState) => {
  const context = buildOnboardingContext(state);
  const baseFlow = getBaseFlow(context);
  const dateOfBirthDebug = getDateOfBirthDebugInfo(context.patientDateOfBirth);

  let currentFlow = baseFlow;
  const transformers: TransformerDebugInfo[] = TRANSFORMERS.map(
    (transformer) => {
      const before = [...currentFlow];
      const after = transformer(before, context);
      currentFlow = after;

      return {
        name: transformer.name || 'anonymousTransformer',
        before,
        after,
        changed: before.join('|') !== after.join('|'),
        inputs: getTransformerInputs(
          transformer,
          before,
          context,
          dateOfBirthDebug,
        ),
      };
    },
  );

  const finalFlow = currentFlow;

  return {
    computedAt: new Date().toISOString(),
    computed: {
      finalFlow,
      onboardingSteps: finalFlow.filter((step) => !isOffboardingStep(step)),
      offboardingSteps: finalFlow.filter((step) => isOffboardingStep(step)),
      onboardingStepCount: finalFlow.filter((step) => !isOffboardingStep(step))
        .length,
      offboardingStepCount: finalFlow.filter((step) => isOffboardingStep(step))
        .length,
    },
    flowComputation: {
      baseFlow,
      transformers,
    },
    context,
    influencingState: {
      questionnaireConfig: {
        patientOnboarding: state.questionnaire.config?.patientOnboarding,
        patientOffboarding: state.questionnaire.config?.patientOffboarding,
      },
      graph: {
        mode: state.graph.mode,
      },
      consultation: {
        noAppointment: state.consultation.noAppointment,
        patient: state.consultation.patient,
        patientInformationCompletionBreakdown:
          getPatientInfoCompletionBreakdown(state),
      },
      searchParams: state.searchParams,
    },
  };
};
